import { z } from 'zod';

/** Mirrors server-side FluentValidation; server remains source of truth (CLAUDE.md 7.6). */
export const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean(),
});

export type LoginRequest = z.infer<typeof loginSchema>;

export interface LoginAuthData {
  token: string;
  refreshToken: string;
  user: { id: string; name: string; email: string };
  /** User's default division Oid — null when tenancy tables are not yet
   *  seeded for this user. Stored in the SPA auth-store at login time so
   *  JobID generation can read it without an extra API call. */
  divisionId?:   string | null;
  /** Short division code (e.g. "MICRO") — companion to divisionId. */
  divisionCode?: string | null;
}

export interface NavPermissionRow {
  navigationView:    string | null;
  navigationCaption: string | null;
  itempath:          string | null;
}

export interface DashboardPermissionRow {
  dashboardId:    string;            // Guid string from DashboardData.Oid
  dashboardTitle: string | null;
}

export interface ServerNavPermissions {
  isAdmin: boolean;
  roles: string;
  allowedNavViews:   NavPermissionRow[];
  allowedDashboards: DashboardPermissionRow[];
  /** Global "Form Setup" gate — formId → visible.  Ids absent from the map are
   *  visible by default.  ANDed with the per-user permissions above. */
  formVisibility?:   Record<string, boolean>;
  /** Data Review nav visibility (Default Settings items 6-9) — inlined into
   *  the login payload so the sidebar has the right answer synchronously,
   *  with no separate app-config fetch/loading window. */
  dataReviewNav?: {
    sdmsResultValidation: boolean;
    sdmsResultApproval: boolean;
    resultValidation: boolean;
    resultApproval: boolean;
    fieldDataValidation: boolean;
    fieldDataApproval: boolean;
  };
  /** ALPACA App Config "AVA Enabled" toggle — drives AVA Insight nav
   *  visibility (parity with the ALPACA header AVA button). */
  avaEnabled?: boolean;
  /** Effective per-view action permissions (Read/Write/Create/Delete) derived
   *  from the user's Module Access Control levels — drives per-form button
   *  gating on the client.  Empty for admins. */
  navActions?: Array<{
    navigationView: string | null;
    canRead:   boolean;
    canWrite:  boolean;
    canCreate: boolean;
    canDelete: boolean;
  }>;
}

/**
 * Server response to POST /api/v1/auth/login.
 *
 * Discriminated union:
 *
 *   • `LoginCompleted` — no 2FA/MFA enrolled: we get the JWT directly.
 *   • `LoginChallengeRequired` — user has 2FA (SMS/Email OTP) or MFA (TOTP):
 *      we get a challengeId + must POST it back to /challenge/verify with the
 *      6-digit code to complete the login.
 *
 * Distinguished by the presence of `requires`.  Old clients pre-2FA always
 * received `LoginCompleted`; the new shape is fully backwards compatible.
 */
export interface LoginCompleted {
  auth:        LoginAuthData;
  permissions: ServerNavPermissions;
}

export interface LoginChallengeRequired {
  requires:           'mfa' | '2fa';
  challengeId:        string;
  /** Only set when requires='2fa'.  Tells the UI which delivery channel
   *  was used so it can render "We sent a code to your email/phone". */
  channel?:           'Mobile' | 'Email';
  /** Only set when requires='2fa'.  e.g. "j***@example.com" — safe to show. */
  maskedDestination?: string;
  /** Server-side TTL in seconds — UI countdown timer. */
  expiresInSeconds?:  number;
  rememberMe?:        boolean;
}

/**
 * ALPACA parity (`TwoFactorAuthNewViewController`'s destination dropdown):
 * under a FORCED 'twofactor' login mode, an admin/service user with more
 * than one registered email on file must pick which one receives the code
 * BEFORE the OTP is sent.  POST the chosen index to
 * /api/v1/auth/2fa/select-destination, which returns a normal
 * `LoginChallengeRequired` (requires='2fa').
 */
export interface LoginDestinationPickRequired {
  requires:          'selectDestination';
  pickId:            string;
  channel:           'Mobile' | 'Email';
  /** Masked, e.g. ["j***@example.com", "a***@example.com"] — safe to show. */
  destinations:      string[];
  expiresInSeconds?: number;
  rememberMe?:       boolean;
}

/**
 * Mode 'mfa' + NO authenticator enrolled yet on this account — first-time
 * setup happens INLINE at login: a fresh QR/secret is issued right here, the
 * user scans it and enters the resulting code via
 * POST /api/v1/auth/mfa/enroll-at-login/verify, which BOTH persists the new
 * secret AND completes login in one step. A shared account (e.g. "service")
 * can have several people each enroll their own device later from the
 * ordinary MFA challenge screen ("Add another authenticator" — see
 * LoginChallengeRequired's sibling flow in LoginPage.tsx).
 */
export interface LoginMfaEnrollRequired {
  requires:          'mfaEnroll';
  challengeId:       string;
  secretBase32:      string;
  otpAuthUri:        string;
  issuer:            string;
  account:           string;
  expiresInSeconds?: number;
  rememberMe?:       boolean;
}

export type LoginResponseData =
  | LoginCompleted
  | LoginChallengeRequired
  | LoginDestinationPickRequired
  | LoginMfaEnrollRequired;

// ─── Login Options (global login mode — Settings → Default Settings) ───────

/** The 5 selectable global login modes.  Working today: standard · twofactor ·
 *  mfa.  sso-saml / ad are selectable in the UI but rejected by the API until
 *  IdP setup exists ("requires IdP setup — coming soon"). */
export type LoginMode = 'standard' | 'twofactor' | 'mfa' | 'sso-saml' | 'ad';

/** GET /api/v1/auth/login-config (anonymous) — what the login page needs
 *  BEFORE auth.  Minimal by design (a future ssoUrl lands here too). */
export interface LoginConfig {
  loginMode: LoginMode;
}

export function isChallengeRequired(r: LoginResponseData): r is LoginChallengeRequired {
  const req = (r as LoginChallengeRequired).requires;
  return req === 'mfa' || req === '2fa';
}

export function isDestinationPickRequired(r: LoginResponseData): r is LoginDestinationPickRequired {
  return (r as LoginDestinationPickRequired).requires === 'selectDestination';
}

export function isMfaEnrollRequired(r: LoginResponseData): r is LoginMfaEnrollRequired {
  return (r as LoginMfaEnrollRequired).requires === 'mfaEnroll';
}

// ─── Challenge-verify (the second leg of 2FA/MFA login) ───────────────────

export interface VerifyChallengeRequest {
  challengeId: string;
  code:        string;
  rememberMe:  boolean;
}

// Verify response is always LoginCompleted (server only mints JWT on success).
export type VerifyChallengeResponseData = LoginCompleted;

// ─── MFA "add another authenticator" (from an already-enrolled account's
// normal MFA challenge screen — e.g. a shared "service" login) ────────────

/** Response to POST /api/v1/auth/mfa/enroll-at-login/start — same shape as
 *  LoginMfaEnrollRequired minus the outer `requires`/`rememberMe` envelope,
 *  since this is requested FROM WITHIN an already-active MFA challenge. */
export interface MfaEnrollAtLoginStartResponse {
  challengeId:      string;
  secretBase32:     string;
  otpAuthUri:       string;
  issuer:           string;
  account:          string;
  expiresInSeconds: number;
}

export type ApiResponse<T> =
  | { success: true; data: T }
  | { success: false; error: { code: string; message: string } };
